import { useState, type ReactNode, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

export type PlaybackSpeed = 0.5 | 0.75 | 1 | 1.25 | 1.5 | 2;
const SPEEDS: PlaybackSpeed[] = [0.5, 0.75, 1, 1.25, 1.5, 2];

interface Slots {
  quality?: ReactNode;
  source?: ReactNode;
  subtitles?: ReactNode;
  buffer?: ReactNode;
}

type TabId = 'speed' | 'quality' | 'source' | 'subtitles' | 'buffer';

interface Props {
  speed: number;
  onSpeedChange: (s: PlaybackSpeed) => void;
  slots: Slots;
  onClose: () => void;
}

export function SettingsPanel({ speed, onSpeedChange, slots, onClose }: Props): JSX.Element {
  const { t } = useTranslation();
  const tabs: { id: TabId; label: string; enabled: boolean }[] = [
    { id: 'speed', label: t('player.tabs.speed'), enabled: true },
    { id: 'quality', label: t('player.tabs.quality'), enabled: slots.quality != null },
    { id: 'source', label: t('player.tabs.source'), enabled: slots.source != null },
    { id: 'subtitles', label: t('player.tabs.subtitles'), enabled: slots.subtitles != null },
    { id: 'buffer', label: t('player.tabs.buffer'), enabled: slots.buffer != null },
  ];
  const [active, setActive] = useState<TabId>('speed');
  // If the currently-active tab has no slot (parent removed it after we
  // navigated there), fall back to speed so the body never renders blank.
  const effectiveActive: TabId = tabs.find((t) => t.id === active && t.enabled) ? active : 'speed';

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  }

  return (
    <div
      className="player-settings-panel"
      data-testid="settings-panel"
      role="dialog"
      aria-label={t('player.settingsPanelLabel')}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <div role="tablist" className="player-settings-panel__tabs">
        {tabs.filter((t) => t.enabled).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={effectiveActive === t.id}
            className="player-settings-panel__tab"
            onClick={(): void => setActive(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="player-settings-panel__body">
        {effectiveActive === 'speed' && (
          <div className="player-settings-panel__speeds">
            {SPEEDS.map((s) => (
              <button
                key={s}
                type="button"
                className="player-settings-panel__speed"
                aria-pressed={Math.abs(speed - s) < 0.01}
                onClick={(): void => onSpeedChange(s)}
              >
                {s}×
              </button>
            ))}
          </div>
        )}
        {effectiveActive === 'quality' && slots.quality}
        {effectiveActive === 'source' && slots.source}
        {effectiveActive === 'subtitles' && slots.subtitles}
        {effectiveActive === 'buffer' && slots.buffer}
      </div>
    </div>
  );
}
